import {
  CommandBarButton,
  DefaultButton,
  Icon,
  IconButton,
  Modal,
  PrimaryButton,
  Spinner,
  Stack,
  Text,
  TooltipHost,
  DirectionalHint,
  Checkbox,
} from "@fluentui/react";

import styles from "./PromptTemplatePanel.module.css";

import { useContext, useState, useEffect, useRef } from "react";
import { AppStateContext } from "../../state/AppProvider";
import {
  deletePromptTemplate,
  getActivePromptTemplateCategoryIds,
  getPromptTemplate,
  getPromptTemplateCategories,
  getPromptTemplates,
  PromptTemplateVar,
} from "../../api";
import { PromptTemplateCategory, PromptTemplate } from "../../api";
import { is_model_usable, is_rag_accessible } from "./EditPromptTemplate";
import { Link } from "react-router-dom";
import { getFactory } from "./VarElementFactory";
import { defaultProcessingMessage } from "./VarElementFactory/VarElementFactory";
import { useIsMobile } from "../../hooks/useIsMobile";

const AddTemplateElement = (props: { category_id: number }) => {
  return (
    <Stack
      id={"template_item_add"}
      verticalAlign="center"
      className={styles.itemCellAdd}
      horizontal
      onClick={() => {}}
    >
      <Link
        to={`/edit_prompt_template?category_id=${props.category_id}`}
        target="_blank"
        className={styles.itemCellText}
        style={{ fontSize: 16, textDecoration: "none" }}
      >
        {"＋　テンプレート追加"}
      </Link>
    </Stack>
  );
};

interface PromptTemplateElementProps {
  template: PromptTemplate;
  onElementDeleteClicked: (template: PromptTemplate) => void;
  onElementClicked: (template: PromptTemplate) => void;
}

const PromptTemplateElement = (props: PromptTemplateElementProps) => {
  const isEditable = props.template.owner_id && props.template.company_cd;
  const id = props.template.id.toString();
  return (
    <TooltipHost
      content={
        props.template.description.length !== 0
          ? props.template.description
          : "説明がありません"
      }
      id={id}
      directionalHint={DirectionalHint.rightCenter}
      delay={0}
      calloutProps={{
        gapSpace: 0,
      }}
      styles={{
        root: {
          display: "inline-block",
        },
      }}
    >
      <Stack
        id={"template_item_" + props.template.id}
        verticalAlign="center"
        className={styles.itemCellTemplate}
        horizontal
        onClick={() => {
          props.onElementClicked(props.template);
        }}
        aria-describedby={id}
      >
        <Text className={styles.itemCellText} style={{ fontSize: 16 }}>
          {props.template.name}
        </Text>
        <Stack style={{ marginLeft: "auto", marginRight: "0px" }} horizontal>
          {isEditable && (
            <CommandBarButton
              className={styles.itemButton}
              iconProps={{ iconName: "Delete" }}
              title="削除"
              onClick={(e) => {
                props.onElementDeleteClicked(props.template);
                e.stopPropagation();
              }}
            />
          )}
          {isEditable && (
            <CommandBarButton
              className={styles.itemButton}
              iconProps={{ iconName: "Edit" }}
              title="編集"
              href={`/edit_prompt_template/${props.template.id}`}
              target="_blank"
              onClick={(e) => {
                e.stopPropagation();
              }}
            />
          )}
          <CommandBarButton
            className={styles.itemButton}
            iconProps={{ iconName: "Copy" }}
            title="このテンプレートを基に新規作成"
            href={`/edit_prompt_template?copy_from=${props.template.id}`}
            target="_blank"
            onClick={(e) => {
              e.stopPropagation();
            }}
          />
        </Stack>
      </Stack>
    </TooltipHost>
  );
};

interface PromptTemplateCategoryElementProps {
  category: PromptTemplateCategory;
  onElementDeleteClicked: (template: PromptTemplate) => void;
  onElementClicked: (template: PromptTemplate) => void;
  refreshTrigger: number;
}

// 個人用テンプレートを後ろへ
const sortTemplates = (templates: PromptTemplate[]) => {
  const publics = templates.filter((v) => v.owner_id == null);
  const privates = templates.filter((v) => v.owner_id != null);
  return publics.concat(privates);
};

const openingCategoriesCache = new Set<number>();

const PromptTemplateCategoryElement = (
  props: PromptTemplateCategoryElementProps,
) => {
  const { category, onElementDeleteClicked, onElementClicked, refreshTrigger } =
    props;
  const [isOpen, setIsOpen] = useState(openingCategoriesCache.has(category.id));
  const [isLoading, setIsLoading] = useState(false);
  const [templates, setTemplates] = useState<PromptTemplate[]>([]);
  const load = () => {
    setIsLoading(true);
    getPromptTemplates(category.id).then((v) => {
      setTemplates(v);
      setIsLoading(false);
    });
  };
  useEffect(() => {
    if (isOpen) {
      load();
    }
  }, [isOpen, refreshTrigger]);

  const handleOpen = (open: boolean) => {
    setIsOpen(open);
    if (open) {
      openingCategoriesCache.add(category.id);
    } else {
      openingCategoriesCache.delete(category.id);
    }
  };

  return (
    <>
      <Stack
        id={"template_category_item_" + category.id}
        verticalAlign="center"
        className={styles.itemCell}
        horizontal
        tokens={{ childrenGap: "10px" }}
        onClick={() => {
          handleOpen(!isOpen);
        }}
      >
        <Icon iconName={isOpen ? "ChevronUp" : "ChevronDown"} />
        <Text className={styles.itemCellText} style={{ fontSize: 16 }}>
          {category.name}
        </Text>
        {isLoading ? <Spinner /> : <></>}
      </Stack>
      {isOpen ? (
        <Stack
          style={{
            maxWidth: "100%",
            marginLeft: "28px",
            backgroundColor: "#f9f9f9",
          }}
        >
          {0 < category.id ? (
            <AddTemplateElement category_id={category.id} />
          ) : (
            <></>
          )}
          {sortTemplates(templates).map((template) => (
            <PromptTemplateElement
              key={template.id}
              template={template}
              onElementClicked={onElementClicked}
              onElementDeleteClicked={onElementDeleteClicked}
            />
          ))}
        </Stack>
      ) : (
        <></>
      )}
    </>
  );
};

const VarElement = (props: { templateVar: PromptTemplateVar }) => {
  const { templateVar } = props;
  const UtilizeModalContent = getFactory(
    templateVar.input_type,
  )?.utilizeModalContent;
  return UtilizeModalContent ? (
    <UtilizeModalContent templateVar={templateVar} />
  ) : (
    <></>
  );
};

export interface PromptTemplateMenuProps {
  onSend: (question: string, files: File[], template_id: number) => void;
  clearCurrentChat: () => void;
  setQuestion: (question: string) => void;
  handleFiles: (files: File[], detectAssistantsApi: boolean) => void;
}

function getSortedVars(template: PromptTemplate) {
  const req_prompt = template.schema.request_prompt;
  const v_priority = new Map<string, number>();
  for (const v of template.schema.vars) {
    const splitted = req_prompt.split(`{{${v.id}}}`).at(0);

    v_priority.set(v.id, splitted?.length ?? Number.MAX_SAFE_INTEGER);
  }
  // 元の配列のコピーを作成してソート（安定ソート）
  return [...template.schema.vars].sort((a, b) => {
    const priorityA = v_priority.get(a.id) ?? Number.MAX_SAFE_INTEGER;
    const priorityB = v_priority.get(b.id) ?? Number.MAX_SAFE_INTEGER;
    return priorityA - priorityB;
  });
}

let enableInactiveCategoryCache = false;

export function PromptTemplateMenu(props: PromptTemplateMenuProps) {
  const { onSend, clearCurrentChat, setQuestion, handleFiles } = props;
  const appStateContext = useContext(AppStateContext);
  const [categories, setCategories] = useState<PromptTemplateCategory[]>([]);
  const [activeCategoryIds, setActiveCategoryIds] = useState<(number | null)[]>(
    [],
  );
  const [enableInactiveCategory, setEnableInactiveCategory] = useState(
    enableInactiveCategoryCache,
  );
  const [isLoading, setIsLoading] = useState(false);
  const [isTemplateLoading, setIsTemplateLoading] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<PromptTemplate | null>(null);
  const [refreshTrigger, setRefreshTrigger] = useState(0); // 再読み込みトリガー
  const [utilizeTarget, setUtilizeTarget] = useState<PromptTemplate | null>(
    null,
  );
  const [errorMes, setErrorMes] = useState<null | string>(null);
  const [varProcessingMes, setVarProcessingMes] = useState<null | string>(null);
  const [isSystemPromptOpen, setIsSystemPromptOpen] = useState(false);
  const formRef = useRef<HTMLFormElement>(null);
  const submitMode = useRef<"Send" | "ReflectToChat">("Send");
  const isMobile = useIsMobile();

  const ragTitle = appStateContext?.state.RAGMenuList?.filter((v) =>
    utilizeTarget?.schema.rag_ids.includes(v.id),
  )
    .map((v) => v.name)
    .join(", ");

  useEffect(() => {
    const fetch_fn = async () => {
      const cats = await getPromptTemplateCategories();
      setCategories(cats);
      const ids = await getActivePromptTemplateCategoryIds();
      setActiveCategoryIds(ids);
    };

    if (appStateContext?.state.isSideBarOpen) {
      setIsLoading(true);
      fetch_fn().finally(() => {
        setIsLoading(false);
      });
    }
  }, [appStateContext?.state.isSideBarOpen]);

  const handleEnableInactiveCategory = (checked: boolean) => {
    setEnableInactiveCategory(checked);
    enableInactiveCategoryCache = checked;
  };

  const handleDeleteClick = (template: PromptTemplate) => {
    setDeleteTarget(template);
  };
  const handleDeleteEnd = () => {
    setDeleteTarget(null);
    setRefreshTrigger((prev) => prev + 1);
  };

  const handleUtilizeTemplate = async (template: PromptTemplate) => {
    setIsTemplateLoading(true);
    const latest = await getPromptTemplate(template.id);
    setIsTemplateLoading(false);
    if (!latest) {
      setErrorMes("プロンプトテンプレートの取得に失敗しました");
      return;
    }

    const modelSpecified = latest.schema.chat_model.length != 0;
    const model = modelSpecified
      ? latest.schema.chat_model
      : appStateContext?.state.selectedGPTModelIndex || "";
    const available = Array.from(
      appStateContext?.state.availableModels.entries() ?? [],
    ).find((v) => v[0] == model);
    if (modelSpecified && !available) {
      setErrorMes(
        "プロンプトテンプレートにて指定されているモデルが利用不能です\nプロンプトテンプレートを修正し、利用可能なモデルを指定してください",
      );
      return;
    }
    const model_info = appStateContext?.state.availableModels.get(model);
    const model_usable =
      model_info && is_model_usable(model_info, latest.schema.tools);
    if (!model_usable) {
      setErrorMes(
        "現在選択中のモデルでは、このプロンプトテンプレートは使用できません\n別のモデルを選択して再度プロンプトテンプレートを使用してください",
      );
      return;
    }
    const ragIds = latest.schema.rag_ids;
    const rag_accessible = await is_rag_accessible(
      ragIds,
      appStateContext?.state.RAGMenuList ?? undefined,
    );
    if (!rag_accessible) {
      setErrorMes("RAGカテゴリへのアクセス権限がありません");
      return;
    }
    clearCurrentChat();
    // UIに反映
    appStateContext?.dispatch({
      type: "SET_IS_RAG_ENABLED",
      payload: {
        isEnabled: ragIds.length !== 0,
        openRAGMenuIfToggleToOpen: false,
      },
    });
    if (0 < ragIds.length) {
      appStateContext?.dispatch({
        type: "SET_RAG_CHAT",
        payload: new Set<number>(ragIds),
      });
    }
    if (0 < latest.schema.chat_model.length) {
      appStateContext?.dispatch({
        type: "SET_SELECTED_GPT_MODEL_INDEX",
        payload: latest.schema.chat_model,
      });
    }
    appStateContext?.dispatch({type: "SET_TOOLS", payload: latest.schema.tools})
    setUtilizeTarget(latest)
  }
  const handleUtilizeTemplateEnd = () => {
    setUtilizeTarget(null);
    setIsSystemPromptOpen(false);
  };

  const handleSendClick = () => {
    submitMode.current = "Send";
    // setTimeout を使って次のイベントループでrequestSubmitを実行
    setTimeout(() => {
      formRef.current?.requestSubmit();
    }, 0);
  };
  const handleReflectClick = () => {
    submitMode.current = "ReflectToChat";
    // setTimeout を使って次のイベントループでrequestSubmitを実行
    setTimeout(() => {
      formRef.current?.requestSubmit();
    }, 0);
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    if (!utilizeTarget) return;
    if (!e.currentTarget.checkValidity()) {
      return;
    }
    e.preventDefault();

    try {
      const formElement = formRef.current;
      if (!formElement) return;

      const formData = new FormData(formElement);
      const files: File[] = [];
      let req_prompt = utilizeTarget.schema.request_prompt;

      // 各変数要素を更新
      for (const v of utilizeTarget.schema.vars) {
        const element = formElement.querySelector(
          `input[name="${v.id}"]`,
        ) as HTMLInputElement;
        const factory = getFactory(v.input_type);
        setVarProcessingMes(
          factory?.processingMessage ?? defaultProcessingMessage,
        );
        const processor = factory?.processVar;
        await processor?.({
          templateVar: v,
          formData: formData,
          element: element,
          requestPrompt: req_prompt,
          onChangeRequestPrompt: (v: string) => {
            req_prompt = v;
          },
          onFileAdded: (f: File) => {
            files.push(f);
          },
        });
      }
      if (isMobile) {
        appStateContext?.dispatch({ type: "SET_SIDEBAR_OPEN", payload: false });
      }
      appStateContext?.dispatch({
        type: "SET_PROMPT_TEMPLATE_OVERRIDE",
        payload: { name: utilizeTarget.name, id: utilizeTarget.id },
      });

      if (submitMode.current == "Send") {
        onSend(req_prompt, files, utilizeTarget.id);
      } else if (submitMode.current == "ReflectToChat") {
        setQuestion(req_prompt);
        handleFiles(files, false);
      }
    } catch (e) {
      setErrorMes(`エラーが発生しました\n${String(e)}`);
    } finally {
      handleUtilizeTemplateEnd();
      setVarProcessingMes(null);
    }
  };

  return (
    <section
      className={styles.container}
      data-is-scrollable
      aria-label={"prompt template menu panel"}
    >
      <Checkbox
        label="すべてのカテゴリを表示する"
        styles={{
          root: {
            padding: "10px 0px",
          },
          text: {
            fontSize: "12.5px",
          },
        }}
        checked={enableInactiveCategory}
        onChange={(_, v) => {
          if (v !== undefined) {
            handleEnableInactiveCategory(v);
          }
        }}
      />
      {isLoading ? (
        <Spinner />
      ) : (
        <Stack
          aria-label="prompt template menu content"
          style={{
            overflowY: "auto",
            flex: 1,
            maxHeight: "100%",
          }}
        >
          {categories
            .filter((cat) => {
              if (enableInactiveCategory) return true;
              return activeCategoryIds.includes(cat.id);
            })
            .map((cat) => (
              <PromptTemplateCategoryElement
                key={cat.id}
                category={cat}
                onElementClicked={handleUtilizeTemplate}
                onElementDeleteClicked={handleDeleteClick}
                refreshTrigger={refreshTrigger}
              />
            ))}
          {(enableInactiveCategory || activeCategoryIds.includes(null)) && (
            <PromptTemplateCategoryElement
              category={{ id: -1, name: "未分類" }}
              onElementClicked={handleUtilizeTemplate}
              onElementDeleteClicked={handleDeleteClick}
              refreshTrigger={refreshTrigger}
            />
          )}
        </Stack>
      )}
      {/* 削除用モーダル */}
      <Modal
        isOpen={Boolean(deleteTarget)}
        onDismiss={() => setDeleteTarget(null)}
        isBlocking={true}
      >
        <div style={{ padding: "20px", minWidth: "300px" }}>
          <Stack tokens={{ childrenGap: "20px" }}>
            <Stack
              horizontal
              horizontalAlign="space-between"
              verticalAlign="center"
            >
              <Text variant="xLarge">削除</Text>
              <IconButton
                iconProps={{ iconName: "Cancel" }}
                onClick={() => setDeleteTarget(null)}
              />
            </Stack>
            <Text>テンプレートを削除します{"\n"}本当によろしいですか？</Text>
            <Stack
              horizontal
              tokens={{ childrenGap: "10px" }}
              horizontalAlign="end"
            >
              <PrimaryButton
                onClick={() => {
                  if (deleteTarget) {
                    deletePromptTemplate(
                      deleteTarget.id,
                      deleteTarget.owner_id ?? undefined,
                    );
                  }
                  handleDeleteEnd();
                }}
                text="削除"
              />
              <DefaultButton onClick={handleDeleteEnd} text="キャンセル" />
            </Stack>
          </Stack>
        </div>
      </Modal>
      <Modal
        isOpen={errorMes !== null}
        onDismiss={() => setErrorMes(null)}
        isBlocking={true}
      >
        <div style={{ padding: "20px", minWidth: "300px", maxWidth: "600px" }}>
          <Stack tokens={{ childrenGap: "20px" }}>
            <Stack
              horizontal
              horizontalAlign="space-between"
              verticalAlign="center"
            >
              <Text variant="xLarge">利用不能</Text>
              <IconButton
                iconProps={{ iconName: "Cancel" }}
                onClick={() => setErrorMes(null)}
              />
            </Stack>
            <div style={{ whiteSpace: "pre-line" }}>{errorMes}</div>
            <Stack horizontal horizontalAlign="end">
              <PrimaryButton onClick={() => setErrorMes(null)} text="閉じる" />
            </Stack>
          </Stack>
        </div>
      </Modal>
      {/* プロンプトの質問入力用モーダル */}
      <Modal
        isOpen={Boolean(utilizeTarget) || isTemplateLoading}
        onDismiss={setUtilizeTarget.bind(null, null)}
        isBlocking={Boolean(varProcessingMes)}
        styles={{
          main: {
            paddingLeft: "20px",
            paddingRight: "20px",
            paddingBottom: "20px",
            minWidth: "300px",
            maxWidth: "900px",
            width: "70vw",
          },
          scrollableContent: {
            overflow: "hidden",
            maxHeight: "100%",
          },
        }}
      >
        {isTemplateLoading ? (
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              minHeight: "200px",
            }}
          >
            <Spinner />
          </div>
        ) : varProcessingMes ? (
          <span
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              minHeight: "200px",
            }}
          >
            <Spinner />
            <Text>{varProcessingMes}</Text>
          </span>
        ) : (
          <form ref={formRef} onSubmit={handleSubmit}>
            <Stack>
              <Stack
                horizontal
                style={{ height: "55px" }}
                horizontalAlign="space-between"
                verticalAlign="center"
                id="utilizemodalcontent-header"
              >
                <h2
                  style={{
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  {utilizeTarget &&
                    `＜${utilizeTarget.name}${ragTitle && "：" + ragTitle}＞`}
                </h2>
                <IconButton
                  iconProps={{ iconName: "Cancel" }}
                  onClick={setUtilizeTarget.bind(null, null)}
                />
              </Stack>
              {utilizeTarget && utilizeTarget.schema.vars.length != 0 && (
                <Stack
                  horizontal
                  verticalAlign="center"
                  tokens={{ childrenGap: "10px" }}
                >
                  <h3>質問</h3>
                  {utilizeTarget?.schema.vars.length !== 0 && (
                    <Text
                      as={"h2"}
                      style={{ color: "red", fontWeight: "bold" }}
                    >
                      * は入力必須項目です
                    </Text>
                  )}
                </Stack>
              )}
              <Stack
                id="utilizemodalcontent-body"
                tokens={{ childrenGap: "20px" }}
                style={{ maxHeight: "70vh", overflowY: "auto" }}
              >
                {utilizeTarget &&
                  (utilizeTarget.schema.vars.length === 0 ? (
                    <Text>質問がありません、確定を押してください</Text>
                  ) : (
                    getSortedVars(utilizeTarget).map((v) => (
                      <VarElement key={v.id} templateVar={v} />
                    ))
                  ))}
              </Stack>
              {/* システムプロンプト表示アコーディオン */}
              {utilizeTarget && utilizeTarget.schema.system_prompt && (
                <Stack style={{ marginTop: "20px" }}>
                  <Stack
                    horizontal
                    verticalAlign="center"
                    tokens={{ childrenGap: "10px" }}
                    onClick={() => setIsSystemPromptOpen(!isSystemPromptOpen)}
                    style={{
                      cursor: "pointer",
                      padding: "10px",
                      backgroundColor: "#f3f2f1",
                      borderRadius: "4px",
                    }}
                  >
                    <Icon
                      iconName={
                        isSystemPromptOpen ? "ChevronDown" : "ChevronRight"
                      }
                    />
                    <Text style={{ fontWeight: "600" }}>
                      システムプロンプト
                    </Text>
                  </Stack>
                  {isSystemPromptOpen && (
                    <Stack
                      style={{
                        marginTop: "8px",
                        padding: "12px",
                        backgroundColor: "#faf9f8",
                        border: "1px solid #edebe9",
                        borderRadius: "4px",
                        maxHeight: "200px",
                        overflowY: "auto",
                        whiteSpace: "pre-wrap",
                        wordBreak: "break-word",
                      }}
                    >
                      <Text>{utilizeTarget.schema.system_prompt}</Text>
                    </Stack>
                  )}
                </Stack>
              )}
              <Stack
                horizontal
                horizontalAlign="end"
                tokens={{ childrenGap: "26px" }}
                style={{ marginTop: "50px" }}
              >
                <PrimaryButton
                  text="送信"
                  split
                  menuProps={{
                    items: [
                      {
                        key: "send",
                        text: "直接送信",
                        iconProps: { iconName: "Send" },
                        onClick: () => handleSendClick(),
                      },
                      {
                        key: "reflect",
                        text: "一度チャット欄に反映",
                        iconProps: { iconName: "Down" },
                        onClick: () => handleReflectClick(),
                      },
                    ],
                  }}
                  styles={{
                    splitButtonDivider: {
                      backgroundColor: "white",
                      width: 1,
                      top: 0,
                      bottom: 0,
                    },
                  }}
                  onClick={handleSendClick}
                />
                <DefaultButton
                  text="キャンセル"
                  style={{ minWidth: "120px" }}
                  onClick={handleUtilizeTemplateEnd}
                />
              </Stack>
            </Stack>
          </form>
        )}
      </Modal>
    </section>
  );
}
